import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useSyncExternalStore,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';

// In-memory state for the explicitly labeled review application. This is not
// durable CRM storage or provider authority. Never put card-entry values here.
// One provider belongs to one signed-in workspace; changing its scope discards
// that workspace's values and late results cannot update the new workspace.
function createReviewStore() {
  const values = new Map<string, unknown>();
  const listeners = new Map<string, Set<() => void>>();
  return {
    read<T>(key: string, initial: () => T): T {
      if (!values.has(key)) values.set(key, initial());
      return values.get(key) as T;
    },
    subscribe(key: string, listener: () => void) {
      const group = listeners.get(key) ?? new Set<() => void>();
      group.add(listener);
      listeners.set(key, group);
      return () => {
        group.delete(listener);
        if (group.size === 0) listeners.delete(key);
      };
    },
    update<T>(key: string, initial: () => T, update: SetStateAction<T>) {
      const previous = this.read(key, initial);
      const next = typeof update === 'function'
        ? (update as (value: T) => T)(previous)
        : update;
      if (Object.is(previous, next)) return;
      values.set(key, next);
      listeners.get(key)?.forEach((listener) => listener());
    },
  };
}

type ReviewStore = ReturnType<typeof createReviewStore>;
const ReviewStateContext = createContext<ReviewStore | null>(null);

export function ReviewStateProvider({
  children,
  scopeKey = 'review',
}: { children: ReactNode; scopeKey?: string }): ReactNode {
  const store = useMemo(createReviewStore, [scopeKey]);
  return <ReviewStateContext.Provider value={store}>{children}</ReviewStateContext.Provider>;
}

export function useReviewState<T>(
  customerId: string,
  key: string,
  initial: () => T,
): [T, Dispatch<SetStateAction<T>>] {
  const shared = useContext(ReviewStateContext);
  const local = useRef<ReviewStore | null>(null);
  if (!local.current) local.current = createReviewStore();
  const store = shared ?? local.current;
  const stateKey = JSON.stringify([customerId, key]);
  const initialRef = useRef(initial);
  initialRef.current = initial;
  const read = useCallback(
    () => store.read(stateKey, () => initialRef.current()),
    [store, stateKey],
  );
  const subscribe = useCallback(
    (listener: () => void) => store.subscribe(stateKey, listener),
    [store, stateKey],
  );
  const value = useSyncExternalStore(subscribe, read, read);
  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    (update) => store.update(stateKey, () => initialRef.current(), update),
    [store, stateKey],
  );
  return [value, setValue];
}

/** Return only to a known internal surface for the same customer. */
export function getReviewReturnPath(
  state: unknown,
  customerId: string,
  fallback = `/current-action/${encodeURIComponent(customerId)}`,
): string {
  const candidate = state && typeof state === 'object' && 'returnTo' in state
    ? (state as { returnTo?: unknown }).returnTo
    : null;
  if (typeof candidate !== 'string' || !candidate.startsWith('/') || candidate.startsWith('//')) {
    return fallback;
  }
  const pathname = candidate.split(/[?#]/, 1)[0];
  if (['/my-work', '/my-activity', '/letters'].includes(pathname)) return candidate;
  const parts = pathname.split('/').filter(Boolean);
  const allowed = new Set([
    'current-action', 'build-order', 'subscriptions', 'colic-coverage',
    'gifts', 'horses', 'relationships', 'history', 'letters', 'calls',
    'live-chat', 'customer-addresses', 'preferences', 'special-follow-ups',
  ]);
  try {
    if (allowed.has(parts[0]) && decodeURIComponent(parts[1] ?? '') === customerId) {
      return candidate;
    }
  } catch {
    // An invalid route is not a return destination.
  }
  return fallback;
}

export interface ReviewReturnTarget {
  path: string;
  state: Record<string, unknown>;
}

/** Keep the originating work/basket with a return, including after a pending save.
 * Popup-open flags are one-shot navigation instructions, not return context.
 * A global screen uses the customer captured by its customer-screen opener;
 * without that context it returns to My work instead of assuming a customer.
 */
export function getReviewReturnTarget(
  state: unknown,
  customerId: string | null,
  fallback?: string,
): ReviewReturnTarget {
  const source = state && typeof state === 'object' && !Array.isArray(state)
    ? state as Record<string, unknown> : {};
  const { openPayment: _openPayment, returnCustomerId: priorCustomerId, ...context } = source;
  const originCustomerId = customerId ?? (typeof priorCustomerId === 'string' && priorCustomerId ? priorCustomerId : null);
  const defaultPath = fallback ?? (originCustomerId ? `/current-action/${encodeURIComponent(originCustomerId)}` : '/my-work');
  const candidate = getReviewReturnPath(source, originCustomerId ?? '', defaultPath);
  const path = customerId === null && candidate.split(/[?#]/, 1)[0] === '/my-activity' ? defaultPath : candidate;
  return { path, state: { ...context, returnTo: path, ...(originCustomerId ? { returnCustomerId: originCustomerId } : {}) } };
}
